import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, TrendingUp, User } from 'lucide-react';
import { dashboardService } from '../../services/dashboardService';

const WorkloadWidget = ({ viewType, userId }) => {
  const [workloadData, setWorkloadData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const handleTeamMemberClick = (member) => {
    // Navigate to cases page filtered by attorney
    navigate(`/cases?attorney=${member.id}`, { 
      state: { 
        filterByAttorney: member.id,
        attorneyName: member.name 
      } 
    });
  };

  const fetchWorkloadData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await dashboardService.getWorkload(viewType);
      setWorkloadData(response.data);
    } catch (error) {
      console.error('Error fetching workload data:', error);
      setWorkloadData(null);
    } finally {
      setLoading(false);
    }
  }, [viewType]);

  useEffect(() => {
    fetchWorkloadData();
  }, [fetchWorkloadData]);

  const getCapacityColor = (capacity) => {
    if (capacity >= 90) return 'text-red-600 bg-red-50';
    if (capacity >= 80) return 'text-amber-600 bg-amber-50';
    if (capacity >= 60) return 'text-gray-600 bg-gray-50';
    return 'text-gray-600 bg-gray-50';
  };

  const getCapacityBarColor = (capacity) => {
    if (capacity >= 90) return 'bg-red-500';
    if (capacity >= 80) return 'bg-amber-500';
    if (capacity >= 60) return 'bg-gray-500';
    return 'bg-gray-400';
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow border p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-4 w-40"></div>
          <div className="space-y-3">
            <div className="h-16 bg-gray-100 rounded"></div>
            <div className="h-16 bg-gray-100 rounded"></div>
            <div className="h-16 bg-gray-100 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow border">
      {/* Widget Header */}
      <div className="p-6 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          {viewType === 'my' ? (
            <>
              <User className="h-5 w-5 text-gray-600 mr-2" />
              My Workload
            </>
          ) : (
            <>
              <Users className="h-5 w-5 text-purple-500 mr-2" />
              Team Workload
            </>
          )}
        </h3>
      </div>

      {/* Widget Content */}
      <div className="p-6">
        {!workloadData ? (
          <div className="text-center py-8">
            <div className="text-gray-400 mb-3">
              <TrendingUp className="h-12 w-12 mx-auto" />
            </div>
            <p className="text-gray-600">Unable to load workload data</p>
          </div>
        ) : workloadData.type === 'personal' ? (
          /* Personal Workload View */
          <div className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-gray-900">{workloadData.total_tasks}</div>
                <div className="text-sm text-gray-600">Active Tasks</div>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-gray-900">{workloadData.completed_this_week}</div>
                <div className="text-sm text-gray-600">Completed This Week</div>
              </div>
            </div>

            {/* Billable Hours Progress */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">Monthly Billable Hours</span>
                <span className="text-sm text-gray-600">
                  {workloadData.billable_hours_this_month} / {workloadData.target_billable_hours}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-gray-500 h-2 rounded-full transition-all duration-300"
                  style={{ 
                    width: `${Math.min((workloadData.billable_hours_this_month / workloadData.target_billable_hours) * 100, 100)}%` 
                  }}
                ></div>
              </div>
            </div>

            {/* Status Breakdown */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">In Progress</span>
                <span className="text-sm font-medium text-yellow-600">{workloadData.in_progress}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Overdue</span>
                <span className="text-sm font-medium text-red-600">{workloadData.overdue}</span>
              </div>
            </div>

            {/* Recent Activity */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">Recent Activity</h4>
              <div className="space-y-2">
                {workloadData.recent_activity?.map((activity, index) => (
                  <div key={index} className="text-sm">
                    <div className="text-gray-900">{activity.task}</div>
                    <div className="text-gray-500">{activity.time}</div>
                  </div>
                )) || (
                  <p className="text-sm text-gray-500">No recent activity</p>
                )}
              </div>
            </div>
          </div>
        ) : (
          /* Firm Workload View */
          <div className="space-y-6">
            {/* Firm Summary */}
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-gray-900">{workloadData.firm_totals.total_tasks}</div>
                <div className="text-sm text-gray-600">Total Active Tasks</div>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-gray-900">{workloadData.firm_totals.total_overdue}</div>
                <div className="text-sm text-gray-600">Overdue Tasks</div>
              </div>
            </div>

            {/* Team Members */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">Team Capacity</h4>
              <div className="space-y-3">
                {workloadData.team_members?.map((member, index) => (
                  <div 
                    key={index} 
                    onClick={() => handleTeamMemberClick(member)}
                    className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 hover:border-blue-300 transition-colors cursor-pointer"
                  >
                    <div className="flex justify-between items-center mb-2">
                      <div>
                        <div className="font-medium text-gray-900">{member.name}</div>
                        <div className="text-sm text-gray-600">{member.role}</div>
                      </div>
                      <div className="text-right">
                        <div className={`text-sm font-medium px-2 py-1 rounded ${getCapacityColor(member.capacity)}`}>
                          {member.capacity}% capacity
                        </div>
                      </div>
                    </div>
                    
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${getCapacityBarColor(member.capacity)}`}
                        style={{ width: `${member.capacity}%` }}
                      ></div>
                    </div>

                    <div className="flex justify-between text-sm text-gray-600">
                      <span>{member.total_tasks} tasks</span>
                      <span>{member.overdue} overdue</span>
                      <span>{member.billable_hours}h billable</span>
                    </div>
                  </div>
                )) || (
                  <p className="text-sm text-gray-500">No team members found</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkloadWidget;